import React, {useState, useMemo} from 'react';
import {BillInterface} from '../../interfaces/customer/BillInterface';
import {CustomerInterface} from '../../interfaces/customer/CustomerInterface';
import {CustomerManagerInterface} from '../../interfaces/customer/CustomerManagerInterface';
import OrderProductTable from './OrderProductTable';
import Money from '../core/Money';
import {billApprove} from '../../api/customer';
import {formatNumber, formatDate} from '../../utils/Utils';
import MoneyInterface from '../../interfaces/core/MoneyInterface';
import {useBillPrint} from './useBillPrint';
import {ProductInterface} from "../../interfaces/catalog/ProductInterface";

interface BillProps {
    bill: BillInterface;
    customer: CustomerInterface | CustomerManagerInterface;
    onBillApproved?: () => void;
}

export default function Bill({bill, customer, onBillApproved}: BillProps) {
    const [showDetails, setShowDetails] = useState(false);
    const [inProgress, setInProgress] = useState(false);
    const [currentBill, setCurrentBill] = useState(bill);
    const {printBill} = useBillPrint(currentBill);

    // Convert bill positions to CustomerOrderPositionInterface format
    const products: ProductInterface[] = useMemo(() => {
        return currentBill.positions.map(position => position.product);
    }, [currentBill.positions]);

    // Calculate totals
    const total = useMemo(() => {
        if (!products || products.length === 0) return {
            weight: 0,
            volume: 0,
            count: 0
        };

        return bill.positions.reduce((acc, product) => {
            const cnt = product.cnt || 0;

            return {
                weight: acc.weight + (product.product.weight * cnt),
                volume: acc.volume + (product.product.volume * cnt),
                count: acc.count + cnt
            };
        }, {
            weight: 0,
            volume: 0,
            count: 0
        });
    }, [products]);

    const handleApprove = async () => {
        if (!window.confirm('Вы действительно хотите подтвердить счет?')) {
            return;
        }

        setInProgress(true);
        try {
            await billApprove(currentBill, customer);
            setCurrentBill({
                ...currentBill,
                status_id: 1
            });
            if (onBillApproved) {
                onBillApproved();
            }
        } catch (error: any) {
            alert(`Ошибка: ${error.message || 'Не удалось подтвердить счет'}`);
        } finally {
            setInProgress(false);
        }
    };

    return (
            <tbody>
                <tr>
                    <td >
                        <span>{currentBill.bill_id}</span>
                    </td>
                    <td>
                        <span>{total.count}</span> шт.
                    </td>
                    <td >
                        <span className={'text-nowrap'}>
                            <Money value={currentBill.total as MoneyInterface} />
                        </span>
                    </td>
                    <td >
                        <span className={'text-nowrap'}>{formatNumber(total.weight)} кг.</span>
                        <br />
                        <span className={'text-nowrap'}>
                            {formatNumber(total.volume)} м<sup>3</sup>
                        </span>
                    </td>
                    <td >
                        <span>{formatDate(currentBill.create_date).split(' ')[0]}</span>
                    </td>
                    <td >
                        {currentBill.status_id === 0 ? (
                            <span className="badge bg-secondary">не согласован</span>
                        ) : (
                            <span className="badge bg-success">согласован</span>
                        )}
                    </td>
                    <td >
                        <div className="btn-group">
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setShowDetails(!showDetails)}
                            >
                                <span>{showDetails ? 'Скрыть' : 'Посмотреть'}</span>
                            </button>
                            {currentBill.status_id === 0 && (
                                <button
                                    className="btn btn-danger btn-sm"
                                    onClick={handleApprove}
                                    disabled={inProgress}
                                >
                                    {inProgress && (
                                        <i className="fa fa-refresh fa-spin"></i>
                                    )}
                                    {' '}
                                    <span>{inProgress ? 'Согласование' : 'Согласовать'}</span>
                                </button>
                            )}
                            <button
                                className="btn btn-outline-secondary text-secondary btn-sm"
                                onClick={printBill}
                            >
                                Скачать
                            </button>
                        </div>
                    </td>
                </tr>
                {showDetails && (
                    <tr>
                        <td
                            colSpan={8}
                            style={{
                                borderLeft: '2px dashed #ee9633',
                                paddingLeft: '20px'
                            }}
                        >
                            <OrderProductTable positions={bill.positions} />
                        </td>
                    </tr>
                )}
            </tbody>
    );
}